<form [formGroup]="personalInfoForm" class="form" [class.disabled]="disabled">
  <app-card>
    <div class="apply-form-content">
      <h2 class="apply-form-content__title">{{ 'profile.personal.essentials' | transloco }}</h2>

      <div class="avatar-field">
        <app-user-avatar
          class="avatar-field__avatar"
          size="lg"
          [editable]="true"
          [loading]="avatarLoading"
          [photo]="avatarPhoto"
          [firstName]="avatarFirstName"
          [lastName]="avatarLastName"
          [identifier]="avatarIdentifier"
          (edit)="avatarEdit.emit()"
          (remove)="avatarRemove.emit()"></app-user-avatar>
        <span class="avatar-field__label">{{ 'pages.profile.avatar.label' | transloco }}</span>
      </div>

      <div class="form__group">
        <app-select
          formControlName="gender"
          [label]="'profile.personal.gender' | transloco"
          [options]="[
            { value: 'db.male', label: ('profile.personal.male' | transloco) },
            { value: 'db.female', label: ('profile.personal.female' | transloco) },
            { value: 'db.other', label: ('profile.personal.other' | transloco) }
          ]"
          [placeholder]="'profile.personal.selectGender' | transloco"
          [required]="true"
          [errorMessage]="
            personalInfoForm.get('gender')?.invalid && (personalInfoForm.get('gender')?.touched || formSubmitted)
              ? ('profile.errors.genderRequired' | transloco)
              : ''
          "></app-select>
      </div>

      <div class="form__row">
        <div class="form__group form__group--half">
          <app-input
            formControlName="title"
            [label]="'profile.personal.titleField' | transloco"
            [hint]="'profile.personal.titleFieldHint' | transloco"
            type="text"></app-input>
        </div>
        <div class="form__group form__group--half">
          <app-input
            formControlName="titleAfterName"
            [label]="'profile.personal.titleAfterName' | transloco"
            [hint]="'profile.personal.titleAfterNameHint' | transloco"
            type="text"></app-input>
        </div>
      </div>

      <div class="form__row">
        <div class="form__group form__group--half">
          <app-input
            formControlName="firstName"
            [label]="'profile.personal.firstName' | transloco"
            type="text"
            [required]="true"
            [errorMessage]="
              personalInfoForm.get('firstName')?.invalid && (personalInfoForm.get('firstName')?.touched || formSubmitted)
                ? ('profile.errors.firstNameRequired' | transloco)
                : ''
            "></app-input>
        </div>

        <div class="form__group form__group--half">
          <app-input
            formControlName="lastName"
            [label]="'profile.personal.lastName' | transloco"
            type="text"
            [required]="true"
            [errorMessage]="
              personalInfoForm.get('lastName')?.invalid && (personalInfoForm.get('lastName')?.touched || formSubmitted)
                ? ('profile.errors.lastNameRequired' | transloco)
                : ''
            "></app-input>
        </div>
      </div>

      <div class="form__row">
        <!-- Email Read-only Display -->
        <div class="form__group form__group--half">
          <div class="form__label">{{ 'profile.personal.email' | transloco }}</div>
          <div class="readonly-field">{{ userEmail }}</div>
        </div>
        <div class="form__group form__group--half">
          <div class="phone-field">
            <app-select
              formControlName="phoneCountry"
              [label]="'profile.personal.phoneCountryCode' | transloco"
              [options]="phoneCountryOptions"
              [required]="true"
              (valueChange)="onPhoneCountryChange($event)"></app-select>
            <app-input
              formControlName="phone"
              [label]="'profile.personal.phone' | transloco"
              type="tel"
              [placeholder]="'profile.personal.phonePlaceholder' | transloco"
              [required]="true"
              pattern="[0-9\s()+./-]*"
              (valueChange)="onPhoneInputChange($event)"
              (blur)="formatPhoneInput()"
              [errorMessage]="
                personalInfoForm.get('phone')?.invalid && (personalInfoForm.get('phone')?.touched || formSubmitted)
                  ? ('profile.errors.phoneRequired' | transloco)
                  : ''
              "></app-input>
          </div>
        </div>
      </div>

      <div class="form__group">
        <app-input
          formControlName="dateOfBirth"
          [label]="'profile.personal.dateOfBirth' | transloco"
          type="date"
          [required]="true"
          [errorMessage]="
            personalInfoForm.get('dateOfBirth')?.touched && personalInfoForm.get('dateOfBirth')?.hasError('required')
              ? ('profile.errors.dateOfBirthRequired' | transloco)
              : personalInfoForm.get('dateOfBirth')?.touched && personalInfoForm.get('dateOfBirth')?.hasError('dateInPast')
              ? ('profile.errors.dateOfBirthInPast' | transloco)
              : ''
          "></app-input>
      </div>

      <div class="separator"></div>

      <h3 class="apply-form-content__subtitle">{{ 'profile.personal.addressTitle' | transloco }}</h3>

      <div class="form__group">
        <label class="form__label">{{ 'profile.personal.searchAddress' | transloco }}</label>
        <app-places-autocomplete
          [placeholder]="'profile.personal.searchAddressPlaceholder' | transloco"
          [types]="['address']"
          (addressSelected)="onAddressSelected($event)"></app-places-autocomplete>
        <p class="form__group__description mt-sm">{{ 'profile.personal.searchAddressDescription' | transloco }}</p>

        @if (!showManualAddressFields) {
        <div class="manual-address-link">
          <button type="button" class="link-button" (click)="showManualFields()">
            {{ 'profile.personal.fillManually' | transloco }}
          </button>
        </div>
        }
      </div>

      @if (showManualAddressFields) {
      <div class="form__row">
        <div class="form__group form__group--half">
          <app-input
            formControlName="streetName"
            [label]="'profile.personal.streetName' | transloco"
            type="text"
            [required]="true"
            [errorMessage]="
              personalInfoForm.get('streetName')?.invalid && (personalInfoForm.get('streetName')?.touched || formSubmitted)
                ? ('profile.errors.streetNameRequired' | transloco)
                : ''
            "></app-input>
        </div>

        <div class="form__group form__group--half">
          <app-input
            formControlName="streetNumber"
            [label]="'profile.personal.streetNumber' | transloco"
            type="text"
            [required]="true"
            [errorMessage]="
              personalInfoForm.get('streetNumber')?.invalid && (personalInfoForm.get('streetNumber')?.touched || formSubmitted)
                ? ('profile.errors.streetNumberRequired' | transloco)
                : ''
            "></app-input>
        </div>
      </div>

      <div class="form__row">
        <div class="form__group form__group--half">
          <app-input
            formControlName="city"
            [label]="'profile.personal.city' | transloco"
            type="text"
            [required]="true"
            [errorMessage]="
              personalInfoForm.get('city')?.invalid && (personalInfoForm.get('city')?.touched || formSubmitted)
                ? ('profile.errors.cityRequired' | transloco)
                : ''
            "></app-input>
        </div>

        <div class="form__group form__group--half">
          <app-input
            formControlName="zipCode"
            [label]="'profile.personal.zipCode' | transloco"
            type="text"
            [required]="true"
            [errorMessage]="
              personalInfoForm.get('zipCode')?.invalid && (personalInfoForm.get('zipCode')?.touched || formSubmitted)
                ? ('profile.errors.zipCodeRequired' | transloco)
                : ''
            "></app-input>
        </div>
      </div>

      <div class="form__group">
        <app-input
          formControlName="country"
          [label]="'profile.personal.country' | transloco"
          type="text"
          [required]="true"
          [errorMessage]="
            personalInfoForm.get('country')?.invalid && (personalInfoForm.get('country')?.touched || formSubmitted)
              ? ('profile.errors.countryRequired' | transloco)
              : ''
          "></app-input>
      </div>
      }

      <!-- Education Level Section - Only shown for authenticated users -->
      <ng-container *ngIf="isAuthenticated">
        <div class="separator"></div>

        <h3 class="apply-form-content__subtitle">{{ 'profile.personal.educationLevelTitle' | transloco }}</h3>

        <div class="form__group">
          <app-select
            formControlName="educationLevelID"
            [label]="'profile.personal.educationLevel' | transloco"
            [placeholder]="'profile.personal.selectEducationLevel' | transloco"
            [options]="educationLevelOptions"
            [required]="false"></app-select>
        </div>
      </ng-container>

      <!-- Job Preferences Section - Only shown for authenticated users -->
      <ng-container *ngIf="isAuthenticated">
        <div class="separator"></div>

        <h3 class="apply-form-content__subtitle">{{ 'profile.personal.jobPreferences' | transloco }}</h3>

        <div class="form__row">
          <div class="form__group form__group--half">
            <app-multi-select
              formControlName="jobType"
              [label]="'profile.personal.jobType' | transloco"
              [options]="jobTypeOptions"
              [placeholder]="'profile.personal.selectJobTypes' | transloco"
              [useTranslationForLabels]="false"
              [showCountAfter]="2"></app-multi-select>
          </div>

          <div class="form__group form__group--half">
            <app-multi-select
              formControlName="selfDescription"
              [label]="'profile.personal.selfDescription' | transloco"
              [options]="selfDescriptionOptions"
              [placeholder]="'profile.personal.selectSelfDescriptions' | transloco"
              [useTranslationForLabels]="false"
              [showCountAfter]="2"></app-multi-select>
          </div>
        </div>

        <div class="form__group">
          <app-multi-select
            formControlName="jobCategory"
            [label]="'profile.personal.jobCategory' | transloco"
            [options]="jobCategoryOptions"
            [placeholder]="'profile.personal.selectJobCategories' | transloco"
            [useTranslationForLabels]="false"
            [showCountAfter]="2"
            height="tall"></app-multi-select>
        </div>

        @if (jobPositionOptions.length > 0) {
        <div class="form__group">
          <app-multi-select
            formControlName="jobPosition"
            [label]="'profile.personal.jobPosition' | transloco"
            [options]="jobPositionOptions"
            [placeholder]="'profile.personal.selectJobPositions' | transloco"
            [useTranslationForLabels]="false"
            [showCountAfter]="2"
            height="tall"></app-multi-select>
        </div>
        }

        <div class="separator"></div>
      </ng-container>

      <h3 class="apply-form-content__subtitle">{{ 'profile.personal.aiConsents.title' | transloco }}</h3>
      <div class="form__group text-left ai-consents">
        <p class="form__group__description">{{ 'profile.personal.aiConsents.description' | transloco }}</p>

        <div class="ai-consents__item" [class.ai-consents__item--updating]="isConsentUpdating(ConsentType.AI_MATCHING)">
          <app-input-checkbox
            formControlName="aiMatching"
            [label]="ConsentType.AI_MATCHING | transloco"
            [disabled]="isConsentUpdating(ConsentType.AI_MATCHING)"></app-input-checkbox>
          <p class="form__group__description">
            {{ 'descriptions.' + ConsentType.AI_MATCHING | transloco }}
            <a class="checkbox-link" [routerLink]="['/', languageRoutingService.getCurrentLanguage(), 'privacy-policy']" target="_blank">
              {{ 'auth.register.privacyPolicy' | transloco }}
            </a>
          </p>
        </div>

        <div class="ai-consents__item" [class.ai-consents__item--updating]="isConsentUpdating(ConsentType.AI_PARSING)">
          <app-input-checkbox
            formControlName="aiParsing"
            [label]="ConsentType.AI_PARSING | transloco"
            [disabled]="isConsentUpdating(ConsentType.AI_PARSING)"></app-input-checkbox>
          <p class="form__group__description">
            {{ 'descriptions.' + ConsentType.AI_PARSING | transloco }}
            <a class="checkbox-link" [routerLink]="['/', languageRoutingService.getCurrentLanguage(), 'privacy-policy']" target="_blank">
              {{ 'auth.register.privacyPolicy' | transloco }}
            </a>
          </p>
        </div>

        <p class="form__group__description mt-sm">
          <small>
            {{ 'profile.personal.aiConsents.settingsLinkPrefix' | transloco }}
            <a [routerLink]="settingsLink">{{ 'profile.personal.aiConsents.settingsLink' | transloco }}</a>
            {{ 'profile.personal.aiConsents.settingsLinkSuffix' | transloco }}
          </small>
        </p>
      </div>

      <!-- Alert card for submission error -->
      <app-alert-card *ngIf="error" class="submission-error-alert" [type]="'error'">
        <div class="alert-content">
          <div class="header">
            <h4>{{ 'profile.toast.updateErrorTitle' | transloco }}</h4>
            <p>{{ error }}</p>
          </div>
        </div>
      </app-alert-card>

      <div class="form__actions d-flex jc-between ai-center mt-xl">
        <app-button type="button" variant="outline" color="secondary" [icon]="true" (click)="cancel()">
          <i class="fas fa-times"></i>
          {{ 'button.cancel' | transloco }}
        </app-button>
        <app-button type="button" [icon]="true" (click)="submit()">
          <i class="fas fa-save"></i>
          {{ 'button.save' | transloco }}
        </app-button>
      </div>
    </div>
  </app-card>
</form>
